import { proxyState, deproxify, isProxyfied, isKnownObject, getProxyKey } from 'proxyequal';
import { updateCacheLine } from './cache';
var nothing = {
  is: 'PROXY_EQUAL_NOTHING'
};
var emptyArray = [];

function addAffected(callGeneration, affected, object) {
  var key = getProxyKey(object);

  if (key.fingerPrint.callGeneration === callGeneration) {
    affected[key.fingerPrint.index].resultAffected.push(key.suffix);
    return true;
  }

  return false;
}

var cycleMap = null;
var userShouldDive = null;
var knownPOD = new WeakMap();

var defaultShouldDiveCheck = function defaultShouldDiveCheck(line, key, object) {
  // default check - is not React owner
  if (key === '_owner' && object.$$typeof) {
    // React-specific: avoid traversing React elements' _owner.
    //  _owner contains circular references
    // and is not needed when comparing the actual elements (and not their owners)
    // .$$typeof and ._store on just reasonable markers of a react element
    return false;
  } // could not ACCESS XMLHttpRequest


  if (typeof XMLHttpRequest !== "undefined" && line.constructor === XMLHttpRequest) {
    return false;
  }

  return true;
};

var shouldDive = function shouldDive(line, key, object) {
  return typeof line === 'object' && !isKnownObject(line) && (isProxyfied(line) || userShouldDive(line, key, object, defaultShouldDiveCheck));
};

function forEachIn(obj, iterator) {
  if (Array.isArray(obj)) {
    obj.forEach(function (_, index) {
      return iterator(index);
    });
  }

  Object.keys(obj).forEach(iterator);
}

function deproxifyResult(callGeneration, result, affected, returnPureValue, deepDive) {
  if (deepDive === void 0) {
    deepDive = false;
  }

  var unaffectedResult = returnPureValue ? result : nothing;

  if (!result) {
    return result;
  }

  if (typeof result === 'object') {
    if (knownPOD.get(result)) {
      return unaffectedResult;
    }

    var isInProxy = isProxyfied(result);

    if (isInProxy) {
      if (addAffected(callGeneration, affected, result)) {
        var preResult = deproxify(result);
        return deepDive ? preResult : deproxifyResult(callGeneration, preResult, affected, true, true);
      }
    }

    var sub = Array.isArray(result) ? [] : {};
    var altered = false;

    if (result && Object.getOwnPropertyDescriptor(result, '__proxyequal_scanEnd')) {
      Object.defineProperty(result, '__proxyequal_scanEnd', {
        value: 'here was spread guard',
        configurable: true,
        enumerable: false
      });
    }

    forEachIn(result, function (i) {
      var data = result[i];
      var newResult = data;

      if (data && shouldDive(data, i, result)) {
        if (!cycleMap.has(data)) {
          cycleMap.set(data, nothing);
          newResult = deproxifyResult(callGeneration, data, affected, false, deepDive);
          cycleMap.set(data, newResult);
        } else {
          newResult = cycleMap.get(data);
        }
      }

      if (data && newResult !== nothing && newResult !== data) {
        altered = true;
        sub[i] = newResult;
      } else {
        sub[i] = data;
      }
    });

    if (altered) {
      return sub;
    }

    knownPOD.set(result, true);
    return unaffectedResult;
  }

  return unaffectedResult;
}

export function callIn(that, cache, args, func, memoizationDepth, proxyMap, options) {
  if (proxyMap === void 0) {
    proxyMap = [];
  }

  if (options === void 0) {
    options = {};
  }

  var proxies = args.map(function (arg, index) {
    if (arg && typeof arg === 'object') {
      var map = proxyMap[index];

      if (!map) {
        return proxyMap[index] = proxyState(arg, {
          callGeneration: proxyMap,
          index: index
        });
      }

      map.reset();
      return map.replaceState(arg);
    }

    return undefined;
  });
  var callArgs = args.map(function (arg, index) {
    return proxies[index] ? proxies[index].state : arg;
  }); //call the real function

  var preResult = func.call.apply(func, [that].concat(callArgs));
  proxies.forEach(function (proxy) {
    return proxy && proxy.seal();
  });
  var spreadDetected = [];
  var affected = proxies.map(function (proxy) {
    if (proxy) {
      if (proxy.spreadDetected !== false) {
        spreadDetected.push(proxy.spreadDetected);
      }

      var _affected = proxy.affected || emptyArray;

      return {
        useAffected: [].concat(_affected),
        resultAffected: []
      };
    }

    return undefined;
  });
  cycleMap = new WeakMap();
  userShouldDive = options.deproxifyShouldDive || defaultShouldDiveCheck;
  var result = deproxifyResult(proxyMap, preResult, affected, true);
  var cacheLine = {
    args: args,
    affected: affected,
    result: result,
    callArgs: callArgs
  };

  if (cache.length < memoizationDepth) {
    cache.push(cacheLine);
  } else {
    updateCacheLine(cache, 0, cacheLine);
  }

  if (spreadDetected.length > 0) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('memoize-state: object spread detected in ', func, '. Keys affected: ', spreadDetected.map(function (key) {
        return key ? key : 'root';
      }), '. This is no-op.');
    }
  }

  return result;
}